import {
  Client,
  PickerDisplayMode,
  PickerFileMetadata,
  PickerOptions,
} from 'filestack-js';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { Box } from 'components/Box/Box';
import { configString, configValue } from 'config/appConfigUtils';
import { getUserType } from 'utils/analytics/helpers/getUserType';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { imageSizeUrl } from 'utils/filestack';
import {
  filestackSupportedMimeTypes,
  filestackSupportedMimeTypesWithoutSVG,
} from 'utils/filestackSupportedMimetypes';
import { getJson } from 'utils/http/getJson';
import { useUserEnvironment } from 'zustand-stores/userEnvironmentStore';

import { SVGConfirmationModal } from './SVGConfirmationModal';

const importFilestack = () =>
  import(
    /* webpackChunkName: "filestack" */
    'filestack-js'
  );

type Props = {
  id?: string;
  name: string;
  onChange: (newImage: ApiFilestackImageData) => void;
  aspectRatio?: number;
  disableCrop?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: (arg0: Record<string, any>) => ReactNode;
  getDimensions?: boolean;
  label?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraAnalyticsData?: Record<string, any>;
  allowSVG?: boolean;
};

const getMockImage = () => {
  const useSVG = Boolean(localStorage.getItem('filestackMockSVG'));
  const filename = useSVG ? 'image.svg' : 'image.jpg';
  const mimetype = useSVG ? 'image/svg+xml' : 'image/jpeg';

  return {
    filename,
    handle: configString('filestack', 'handleOverride'),
    mimetype,
    size: 409609,
    source: 'local_file_system',
  };
};

export function FilestackPicker({
  id: idProp,
  name,
  onChange,
  aspectRatio,
  disableCrop,
  children,
  getDimensions,
  label,
  extraAnalyticsData,
  allowSVG,
  ...props
}: Props) {
  const { user } = useUserEnvironment();

  const ref = useRef(null);

  const [showSVGConfirmationModal, setShowSVGConfirmationModal] =
    useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [filestackClient, setFilestackClient] = useState<Client>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const svgResolveFunction = useRef<(() => void) | null>(null);
  const svgRejectFunction = useRef<(() => void) | null>(null);

  // filestack is supposed to accept a node as container but it's not working
  const id = idProp || `fs-picker-${name.replace(/\./g, '_')}`;
  const inputId = `${id}-input`;

  const isFilestackMock = configValue('filestack', 'mock');

  const onMouseEnter = () => {
    setDragActive(true);
  };

  const onMouseLeave = () => {
    setDragActive(false);
  };

  const setInputId = useCallback(() => {
    // accessibility hack
    const el = ref.current;

    if (el) {
      const interval = window.setInterval(() => {
        // @ts-expect-error TS(2339): Property 'querySelector' does not exist on type 'n... Remove this comment to see the full error message
        const inputEl = el.querySelector('input');

        if (inputEl) {
          inputEl.id = inputId;
          inputEl.addEventListener('focus', onMouseEnter);
          inputEl.addEventListener('blur', onMouseLeave);
          window.clearInterval(interval);
        }
      }, 200);
    }
  }, [inputId]);

  const maybeTrackUpload = useCallback(
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (file: Record<string, any>) => {
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (extraAnalyticsData && extraAnalyticsData.skipTracking) {
        return;
      }

      let fileExtension;

      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (file && file.filename) {
        fileExtension = file.filename.split('.').pop();
      }

      trackEvent('Success: Uploaded Image', {
        user_type: getUserType(user),
        file_extension: fileExtension,
        ...extraAnalyticsData,
      });
    },
    [user, extraAnalyticsData],
  );

  const onSuccess = useCallback(
    async (files: PickerFileMetadata[]) => {
      setError(undefined);
      setLoading(false);

      const file = files[0];
      if (!file) return;

      const { filename, handle, mimetype, size, source } = file;
      const newImage = { filename, handle, mimetype, size, source };
      maybeTrackUpload(file);

      if (file && getDimensions) {
        const { width, height } = await getJson<{
          width: number;
          height: number;
        }>(imageSizeUrl({ handle: file.handle }));

        onChange({ ...newImage, width, height });
      } else {
        onChange(newImage);
      }
    },
    [getDimensions, onChange, maybeTrackUpload],
  );

  const onError = (files: PickerFileMetadata[]) => {
    if (files?.length) {
      setError(getText('There was an error uploading your image.'));
    }
  };

  const onFileSelected = useCallback(
    (file: { mimetype: string; originalFile?: object | File }) =>
      new Promise((resolve, reject) => {
        if (allowSVG && file.mimetype.includes('image/svg')) {
          setShowSVGConfirmationModal(true);
          svgResolveFunction.current = () => resolve(file);
          svgRejectFunction.current = () => reject();
        } else if (!allowSVG && file.mimetype.includes('image/svg')) {
          setError(getText('SVG files are not allowed'));
          reject();
        } else {
          if (!file.originalFile) {
            resolve(file);
            return;
          }
          // Load the image as an element to get the dimensions.
          const url = URL.createObjectURL(file.originalFile as Blob);
          const img = new Image();
          img.onload = (event) => {
            const element = event.target as HTMLImageElement;
            URL.revokeObjectURL(url);
            if (element.naturalHeight > 10000 || element.naturalWidth > 10000) {
              setError(getText('Image dimensions must be within 10000px'));
              reject();
            } else {
              resolve(file);
            }
          };
          img.src = url;
        }
      }),
    [allowSVG],
  );

  const baseOptions: PickerOptions = useMemo(
    () => ({
      accept: allowSVG
        ? filestackSupportedMimeTypes
        : filestackSupportedMimeTypesWithoutSVG,
      fromSources: ['local_file_system'],
      onFileSelected,
      uploadInBackground: false,
      exposeOriginalFile: true,
      transformations: {
        crop: aspectRatio
          ? {
              aspectRatio,
              force: true,
            }
          : !disableCrop,
      },
    }),
    [allowSVG, aspectRatio, disableCrop, onFileSelected],
  );

  const fallbackClickHandler = () => {
    if (isFilestackMock) {
      const mockImage = getMockImage();
      maybeTrackUpload(mockImage);
      onChange(mockImage);
    }
  };

  const onClick = () => {
    if (isFilestackMock) {
      onChange(getMockImage());
    }
  };

  useEffect(() => {
    if (isFilestackMock) return;

    const options = {
      ...baseOptions,
      displayMode: PickerDisplayMode.dropPane,
      container: `#${id}`,
      dropPane: {
        overlay: false,
        onSuccess,
        onError,
        onDragEnter: () => setDragActive(true),
        onDragLeave: () => setDragActive(false),
        onDrop: () => setDragActive(false),
      } satisfies PickerOptions['dropPane'],
      onUploadStarted: () => setLoading(true),
    } satisfies PickerOptions;

    if (filestackClient) return;

    importFilestack().then((m) => {
      // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
      const client = m.init(configValue('filestack', 'apiKey'));

      if (document.getElementById(id)) {
        client.picker(options).open();
        setFilestackClient(client);
        setInputId();
      }
    });
  }, [
    disableCrop,
    filestackClient,
    baseOptions,
    id,
    onSuccess,
    setInputId,
    isFilestackMock,
  ]);

  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box position="relative" {...props}>
      {children({ onClick, dragActive, error, loading })}

      <div ref={ref}>
        <label style={{ display: 'none' }} htmlFor={inputId}>
          {label}
        </label>

        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          overflow="hidden"
          opacity={0}
          id={id}
          onClick={fallbackClickHandler}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </div>

      {showSVGConfirmationModal && (
        <SVGConfirmationModal
          onConfirm={() => {
            if (svgResolveFunction.current) {
              svgResolveFunction.current();
              setShowSVGConfirmationModal(false);
            }
          }}
          onClose={() => {
            if (svgRejectFunction.current) {
              svgRejectFunction.current();
              setShowSVGConfirmationModal(false);
            }
          }}
        />
      )}
    </Box>
  );
}
